import { useEffect, useCallback, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Storage } from 'site-modules/shared/utils/storage';
import { APPRAISAL_STORAGE_KEYS } from 'site-modules/shared/components/appraisal/trade-in-appraiser-steps/license-plate-and-vin-step/constants';
import { EventToolbox } from 'client/utils/event-toolbox';
import { STEP_SUBMITTED_WITH_ERRORS } from 'site-modules/shared/constants/appraisal/events';
import { LOCATION_AND_STYLE } from 'site-modules/shared/constants/appraisal/appraisal';
import { getModelState } from 'client/data/luckdragon/model';
import { VehicleModel } from 'client/data/models/vehicle-v2';
import { getAppraiserStylesPath, selectEmo4182Context } from 'client/data/utils/appraiser-preload-experiment';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { focusFirstInputWithError } from './location-and-style-step-utils';

export const useLocationAndStyleStepStorage = () => {
  const storage = useMemo(() => new Storage('sessionStorage'), []);

  useEffect(
    () => () => {
      storage.remove(APPRAISAL_STORAGE_KEYS.LICENSE_PLATE);
      storage.remove(APPRAISAL_STORAGE_KEYS.STATE_CODE);
      storage.remove(APPRAISAL_STORAGE_KEYS.VIN);
      storage.remove(APPRAISAL_STORAGE_KEYS.APPRAISAL_TABS_ACTIVE_TAB);
    },
    [storage]
  );

  return storage;
};

export const useStepLoadingHandler = (isExtendedReport = true) => {
  const [isLoaded, setIsLoaded] = useState(false);

  const handleStepSubmittedWithErrors = useCallback(
    ({ detail }) => {
      if (!isExtendedReport || detail?.stepId !== LOCATION_AND_STYLE) {
        return;
      }
      focusFirstInputWithError(detail.errorMap);
    },
    [isExtendedReport]
  );

  useEffect(() => {
    const handleDomReady = () => {
      setIsLoaded(true);
    };

    EventToolbox.domReady(handleDomReady);
    EventToolbox.on(STEP_SUBMITTED_WITH_ERRORS, handleStepSubmittedWithErrors);

    return () => {
      EventToolbox.off(STEP_SUBMITTED_WITH_ERRORS, handleStepSubmittedWithErrors);
    };
  }, [handleStepSubmittedWithErrors]);

  return isLoaded;
};

/**
 * Hook to determine if style dropdown options are ready.
 * Uses useSelector to get allStyles from Redux via Luckdragon model.
 *
 * Styles are considered ready if:
 * - allStyles loaded from Redux
 *
 * @param {Object} options
 * @param {Object} options.params - Route params with make, model, year
 * @returns {boolean}
 */
export const useAreStylesReady = ({ params }) => {
  const allStyles = useSelector(state => {
    if (!params?.make || !params?.model || !params?.year) {
      return null;
    }

    const stylesPath = getAppraiserStylesPath({
      ...selectEmo4182Context(state),
      make: makeNiceName(params.make),
      model: makeNiceName(params.model),
      year: params.year,
    });
    return getModelState(state).get(stylesPath, VehicleModel);
  });

  return Array.isArray(allStyles);
};
